'use client'

import React from 'react'
import { Section } from '@/components/Section'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'
import { cn } from '@/utilities/ui'
import { ShardGrid } from '@/components/ShardGrid'
import { AbstractNavIcon } from '@/components/icons/AbstractNavIcon'
import { CMSLink } from '@/components/Link'
import { SeeAllStepsBlock } from '@/payload-types'
import { Eyebrow } from '@/components/Eyebrow'
import { Callout } from '@/components/Callout'
import RichText from '@/components/RichText'
import { AbstractIcon } from '@/components/icons/AbstractIcon'

export function SeeAllStepsClient({
  data,
  stats,
}: {
  data: SeeAllStepsBlock
  stats: { totalStorage: string; usedStorage: string }
}) {
  return (
    <Section bgClassName="bg-rock-1">
      <div className="container mx-auto">
        <div className="mb-12">
          {data.heading && data.description && (
            <Callout heading={data.heading} content={data.description} variant="dark" />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] mb-[20px]">
          <div className={cn('p-8 rounded-md relative overflow-hidden bg-olive-2')}>
            <div className="flex flex-col h-full justify-between gap-[30px]">
              <div className="flex justify-between flex-wrap gap-x-4 gap-y-[30px] items-start">
                <Heading size="h3" className={cn('max-w-[300px]', 'text-highlighter')}>
                  {data.store.heading}
                </Heading>
                <CMSLink {...data.store.link} variant="punch" />
              </div>
              <ShardGrid className="h-[200px]" minGapSize={50} />
            </div>
          </div>

          <div className={cn('p-8 rounded-md relative overflow-hidden bg-paper-1')}>
            <div className="flex flex-col h-full justify-between gap-[30px]">
              <div className="flex justify-between flex-wrap gap-x-4 gap-y-[30px] items-start">
                <Heading size="h3" className={cn('max-w-[300px]', 'text-dark')}>
                  {data.provide.heading}
                </Heading>
                <CMSLink {...data.provide.link} variant="olive" />
              </div>
              <ShardGrid className="h-[200px]" minGapSize={50} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-rock-2 p-8">
            <div className="flex flex-col gap-[30px]">
              <AbstractNavIcon icon="favicon" className="w-[64px] h-[64px]" />
              <div className="flex flex-col gap-[20px]">
                <Heading className="mb-2 text-dark text-heading-h4 xl:text-heading-h3">
                  {data.list.heading}
                </Heading>
                {data.list.description && <RichText data={data.list.description} />}
              </div>
              <div className="flex justify-start">
                <CMSLink {...data.list.link} variant="outline-dark" />
              </div>
            </div>
          </div>

          <div className="bg-rock-2 p-8">
            <div className="flex flex-col gap-[30px]">
              <AbstractIcon icon="about" className="bg-rock-2 w-[64px] h-[64px]" />
              <div className="flex flex-col gap-[20px]">
                <Heading className="mb-2 text-dark text-heading-h4 xl:text-heading-h3">
                  {data.wallet.heading}
                </Heading>
                {data.wallet.description && <RichText data={data.wallet.description} />}
              </div>
              <div className="flex justify-start">
                <CMSLink {...data.wallet.link} variant="outline-dark" />
              </div>
            </div>
          </div>

          <div className="bg-rock-2 p-8">
            <div className="flex flex-col gap-[30px]">
              <AbstractNavIcon icon="docs_square" className="w-[64px] h-[64px]" />
              <div className="flex flex-col gap-[20px]">
                <Heading className="mb-2 text-dark text-heading-h4 xl:text-heading-h3">
                  {data.docs.heading}
                </Heading>
                {data.docs.description && <RichText data={data.docs.description} />}
              </div>
              <div className="flex justify-start">
                <CMSLink {...data.docs.link} variant="outline-dark" />
              </div>
            </div>
          </div>

          <div className="bg-rock-2 p-8">
            <div className="flex flex-col gap-[30px] justify-between h-full">
              <Eyebrow label="STORAGE STATS" color="punch" variant="dark" />
              <div className="flex flex-col gap-[50px]">
                <div className="flex flex-col gap-[10px]">
                  <Heading className="text-heading-h4 xl:text-heading-h3">
                    {stats.totalStorage}
                  </Heading>
                  <Text>total storage</Text>
                </div>
                <div className="flex flex-col gap-[10px]">
                  <Heading className="text-heading-h4 xl:text-heading-h3">
                    {stats.usedStorage}
                  </Heading>
                  <Text>used storage</Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}
